<script setup>
import { computed, ref } from 'vue'
import { configuration } from '@dpgradio/creative'
import Map from './components/Map.ce.vue'
import { getCampaign } from './campaigns/index.js'
import { loadSettings } from './settings.js'
import { useLocationStore } from './stores/locations.js'

// Each attribute falls back to the URL parameter with the same (camelCased) name
const props = defineProps({
  stationId: String,
  campaignId: String,
  trackerConfig: String,
  trackerUuid: String,
  liveLocationConfig: String,
  liveLocationUuid: String,
  trackerColor: String,
  trackerIcon: String,
  trackerIconSize: String,
  trackerAnchor: String,
  maxZoom: String,
  padding: String,
  zoomDuration: String,
  allowMove: String,
  sleepingOverlayEnabled: String,
  styleUrl: String,
  updateDocumentTitle: Boolean,
})

const settings = loadSettings(props)

const campaign = getCampaign(settings.campaignId)

const parseConfig = (json, type) => {
  try {
    return JSON.parse(json).map((tracker) => ({ ...tracker, type }))
  } catch (error) {
    console.warn('Failed to parse tracker config:', error)
    return []
  }
}

const getRequestedTrackers = () => {
  const trackers = []

  if (settings.trackerConfig) {
    trackers.push(...parseConfig(settings.trackerConfig, 'locationTracker'))
  } else if (settings.trackerUuid) {
    trackers.push({ uuid: settings.trackerUuid, type: 'locationTracker' })
  }

  if (settings.liveLocationConfig) {
    trackers.push(...parseConfig(settings.liveLocationConfig, 'liveLocation'))
  } else if (settings.liveLocationUuid) {
    trackers.push({ uuid: settings.liveLocationUuid, type: 'liveLocation' })
  }

  return trackers
}

const withDisplayDefaults = (tracker) => ({
  uuid: tracker.uuid,
  type: tracker.type,
  icon: tracker.icon ?? settings.trackerIcon,
  iconSize: tracker.iconSize ?? settings.trackerIconSize,
  color: tracker.color ?? settings.trackerColor,
  anchor: tracker.anchor ?? settings.trackerAnchor,
})

const getTrackers = () => {
  const requestedTrackers = getRequestedTrackers()
  const trackers = (requestedTrackers.length ? requestedTrackers : campaign?.dario?.trackers ?? []).map(
    withDisplayDefaults
  )

  if (!trackers.length) console.warn('No tracker config found')
  return trackers
}

const trackers = getTrackers()
const locations = useLocationStore()
const isConfigured = ref(false)

const initialize = async () => {
  if (props.updateDocumentTitle) document.title = campaign?.title ?? 'Location Tracker Map'

  try {
    await configuration.retrieveConfigForStation(settings.stationId)
  } catch (error) {
    alert(`
      Could not load configuration.
      - Have you set up a configuration for this application in Dario (see [updateConfigSchema.js])? If you do not want a custom configuration, remove the application name from the [configuration.retrieveConfigForDetectedStation] call in [App.ce.vue].
      - Did you provide a station name? (e.g. through the attribute 'station-id' or the URL parameter 'stationId')

      ${error}
    `)
  }

  locations.initTrackers(trackers)
  isConfigured.value = true
}

initialize()

const campaignMap = campaign?.map
const sleepingOverlay = campaign?.sleepingOverlay
const sleepingOverlayComponent = sleepingOverlay?.component
const isSleepingOverlayEnabled = settings.sleepingOverlayEnabled ?? sleepingOverlay?.enabledByDefault === true

// Preview aid for the hosted page only, so it is deliberately not an attribute
const showSleepingOverlay = new URLSearchParams(window.location.search).get('showSleepingOverlay') === 'true'
const previewMessage = 'We slapen even\nMorgen zijn we terug'

const sleeping = computed(() => {
  if (!sleepingOverlayComponent) return null
  const disabled = Object.values(locations.status).find(({ enabled }) => enabled === false)
  if (showSleepingOverlay) return { message: disabled?.message || previewMessage }
  if (!isSleepingOverlayEnabled) return null
  return disabled
})
</script>

<template>
  <template v-if="isConfigured">
    <Map
      v-if="trackers.length"
      :trackers="trackers"
      :maxZoom="settings.maxZoom"
      :padding="settings.padding"
      :zoomDuration="settings.zoomDuration"
      :allowMove="settings.allowMove"
      :styleUrl="settings.styleUrl"
      :mapTheme="campaignMap?.theme"
      :regionBounds="campaignMap?.regionBounds"
    />
    <div v-else class="no-trackers-error">No trackers specified</div>
    <component :is="sleepingOverlayComponent" v-if="sleeping" :message="sleeping.message" />
  </template>
</template>

<style lang="scss">
:host {
  display: block;
  position: relative;
}

.no-trackers-error {
  font-family: sans-serif;

  border: 1px solid red;
  padding: 1em;
  margin: 1em;
  border-radius: 0.5em;
  color: red;
  font-weight: bold;
  text-align: center;
  background-color: #ffe7e7;
}
</style>
